import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { settings, loadSettings } = useAuth();
  const [org, setOrg] = useState(settings);
  const [cats, setCats] = useState(null);
  const [newCat, setNewCat] = useState("");

  useEffect(() => { setOrg(settings); }, [settings]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  const saveOrg = async () => {
    try {
      await api.put("/settings", { product_name: org.product_name, currency: org.currency, thresholds: org.thresholds, alerts: org.alerts });
      toast.success("Settings saved"); loadSettings();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const addCat = async () => {
    if (!newCat.trim()) return;
    try { const { data } = await api.post("/categories", { name: newCat, active: true }); setCats([...cats, data].sort((a, b) => a.name.localeCompare(b.name))); setNewCat(""); toast.success("Category added"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  const toggleCat = async (c) => {
    const { data } = await api.put(`/categories/${c.id}`, { name: c.name, active: !c.active });
    setCats(cats.map((x) => (x.id === c.id ? data : x)));
  };

  const alerts = org.alerts || {};

  return (
    <div className="space-y-6 max-w-3xl">
      <div><h1 className="font-head text-2xl lg:text-3xl font-semibold text-slate-900">Settings</h1><p className="text-slate-500 text-sm mt-1">Configure the product, categories, budget thresholds and alerts.</p></div>

      <Tabs defaultValue="general">
        <TabsList className="h-auto flex-wrap">
          <TabsTrigger value="general" data-testid="settings-tab-general">General</TabsTrigger>
          <TabsTrigger value="categories" data-testid="settings-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="thresholds" data-testid="settings-tab-thresholds">Thresholds & Alerts</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-4">
          <Card className="border-border p-5 space-y-4">
            <div><Label>Product name</Label><Input data-testid="set-product-name" value={org.product_name || ""} onChange={(e) => setOrg({ ...org, product_name: e.target.value })} className="mt-1.5" /><p className="text-xs text-slate-400 mt-1">The name shown throughout the app. Can be renamed anytime.</p></div>
            <div><Label>Currency symbol</Label><Input data-testid="set-currency" value={org.currency || ""} onChange={(e) => setOrg({ ...org, currency: e.target.value })} className="mt-1.5 w-24" /></div>
            <Button data-testid="save-general" onClick={saveOrg} className="bg-[#0F172A] hover:bg-[#1E293B]">Save</Button>
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <Card className="border-border p-5">
            <div className="flex gap-2 mb-4">
              <Input data-testid="new-category" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" />
              <Button data-testid="add-category" onClick={addCat} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
            {!cats ? <Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /> : (
              <Table>
                <TableHeader><TableRow><TableHead>Category</TableHead><TableHead className="text-right">Active</TableHead></TableRow></TableHeader>
                <TableBody>{cats.map((c) => (
                  <TableRow key={c.id} data-testid={`cat-row-${c.id}`}>
                    <TableCell className="text-slate-800">{c.name}</TableCell>
                    <TableCell className="text-right"><Switch checked={c.active} onCheckedChange={() => toggleCat(c)} data-testid={`cat-toggle-${c.id}`} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="thresholds" className="mt-4">
          <Card className="border-border p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Amber threshold (% used)</Label><Input data-testid="set-amber" type="number" value={org.thresholds?.amber ?? 70} onChange={(e) => setOrg({ ...org, thresholds: { ...org.thresholds, amber: parseFloat(e.target.value) } })} className="mt-1.5" /></div>
              <div><Label>Red threshold (% used)</Label><Input data-testid="set-red" type="number" value={org.thresholds?.red ?? 90} onChange={(e) => setOrg({ ...org, thresholds: { ...org.thresholds, red: parseFloat(e.target.value) } })} className="mt-1.5" /></div>
            </div>
            <div className="space-y-3 pt-2 border-t border-border">
              <AlertRow label="Alert at 80% of allocation" checked={alerts.usage_80} onChange={(v) => setOrg({ ...org, alerts: { ...alerts, usage_80: v } })} />
              <AlertRow label="Alert at 90% of allocation" checked={alerts.usage_90} onChange={(v) => setOrg({ ...org, alerts: { ...alerts, usage_90: v } })} />
              <AlertRow label="Alert when allocation exceeded" checked={alerts.exceeded} onChange={(v) => setOrg({ ...org, alerts: { ...alerts, exceeded: v } })} />
              <AlertRow label="Alert on missing receipts" checked={alerts.missing_receipt} onChange={(v) => setOrg({ ...org, alerts: { ...alerts, missing_receipt: v } })} />
              <div className="flex items-center justify-between"><Label>Large transaction threshold ({org.currency})</Label><Input type="number" value={alerts.large_txn ?? 150} onChange={(e) => setOrg({ ...org, alerts: { ...alerts, large_txn: parseFloat(e.target.value) } })} className="w-28" data-testid="set-large-txn" /></div>
              <div className="flex items-center justify-between"><Label>Approval overdue after (days)</Label><Input type="number" value={alerts.approval_days ?? 3} onChange={(e) => setOrg({ ...org, alerts: { ...alerts, approval_days: parseFloat(e.target.value) } })} className="w-28" data-testid="set-approval-days" /></div>
            </div>
            <Button data-testid="save-thresholds" onClick={saveOrg} className="bg-[#0F172A] hover:bg-[#1E293B]">Save</Button>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

const AlertRow = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between"><Label>{label}</Label><Switch checked={!!checked} onCheckedChange={onChange} /></div>
);
